import { startCase } from 'lodash';

import { parseDate } from 'lib/date';

// Lifecycle status of the whole run (design §11). Kept as a string union so an
// unknown future status from the API degrades gracefully rather than throwing.
export enum WorkflowStatus {
  RUNNING = 'running',
  ACTION_REQUIRED = 'action_required',
  COMPLETED = 'completed',
  CANCELED = 'canceled',
}

// Status of a single action (design §5/Appendix C). Phase 0's manual flow mostly
// exercises pending / action_required / completed.
export enum ActionStatus {
  PENDING = 'pending',
  FLAGGED = 'flagged',
  CANCELED = 'canceled',
  SKIPPED = 'skipped',
  ACTION_REQUIRED = 'action_required',
  RUNNING = 'running',
  COMPLETED = 'completed',
  ERRORED = 'errored',
}

// `outcome` is a free-form string the engine never interprets (design §11);
// referral intake writes accepted / rejected, undecided is "none".
export const NO_OUTCOME = 'none';

export interface WorkflowCategoryOptions {
  key: string;
  name: string;
  position: number;
}

export interface WorkflowDefinitionOptions {
  apiName: string;
  displayName: string;
  categories: WorkflowCategoryOptions[];
}

// The declared input snapshot an action ran with (P1-1). An array (not a map) so
// `name` strings stay snake_case and line up with values[].name for the diff.
export interface WorkflowInputOptions {
  name: string;
  value: string | null;
}

export interface WorkflowActionOptions {
  id: string;
  actionType: string;
  status: ActionStatus;
  categoryKey: string | null;
  position: number | null;
  groupId: string | null;
  // P1-1 staleness axis: orthogonal to status — a stale action stays `completed`.
  stale: boolean;
  // P1-2 flagged axis: the declared value names the engine couldn't derive, so this
  // action can't run until a person provides them. Empty unless status is `flagged`.
  unobtainableValues: string[];
  inputs: WorkflowInputOptions[];
}

export interface WorkflowInputDiff {
  name: string;
  ranWith: string | null;
  current: string | null;
}

export interface WorkflowValueOptions {
  name: string;
  value: string | null;
  source: string;
  groupId: string | null;
  // The catalog display label (design P1-5); null when the API hasn't supplied one.
  label: string | null;
}

// The groups (buildings) a run is addressed to. `groupType` carries the kind's
// api_name + display label (e.g. "SNF"); null when the group has no type.
export interface WorkflowGroupTypeOptions {
  apiName?: string;
  displayName?: string;
}

export interface WorkflowGroupOptions {
  id: string;
  name: string;
  groupType?: WorkflowGroupTypeOptions | null;
}

// The current packet (the attachments behind the attachment_ids value), so the edit
// screen can show what's attached before replacing it.
export interface WorkflowAttachmentOptions {
  id: string;
  filename: string | null;
  docType: string | null;
}

export interface WorkflowOptions {
  id: string;
  status: WorkflowStatus;
  outcome: string;
  createdAt: string | Date | null;
  definition: Partial<WorkflowDefinitionOptions>;
  actions: Partial<WorkflowActionOptions>[];
  values: Partial<WorkflowValueOptions>[];
  groups: Partial<WorkflowGroupOptions>[];
  attachments: Partial<WorkflowAttachmentOptions>[];
}

function getDefaults(): WorkflowOptions {
  return {
    id: '',
    status: WorkflowStatus.RUNNING,
    outcome: NO_OUTCOME,
    createdAt: null,
    definition: {},
    actions: [],
    values: [],
    groups: [],
    attachments: [],
  };
}

export class WorkflowAction {
  id: string;
  actionType: string;
  status: ActionStatus;
  categoryKey: string | null;
  position: number | null;
  groupId: string | null;
  stale: boolean;
  unobtainableValues: string[];
  inputs: WorkflowInputOptions[];

  constructor(opts: Partial<WorkflowActionOptions> = {}) {
    this.id = opts.id ?? '';
    this.actionType = opts.actionType ?? '';
    this.status = opts.status ?? ActionStatus.PENDING;
    this.categoryKey = opts.categoryKey ?? null;
    this.position = opts.position ?? null;
    this.groupId = opts.groupId ?? null;
    this.stale = opts.stale ?? false;
    this.unobtainableValues = opts.unobtainableValues ?? [];
    this.inputs = (opts.inputs ?? []).map((i) => ({ name: i.name, value: i.value ?? null }));
  }

  // `WorkflowActionDefinition::ClinicalReview` -> `ClinicalReview`
  get kind(): string {
    return this.actionType.split('::').pop() ?? this.actionType;
  }

  // A readable fallback label derived from the kind (`ClinicalReview` -> `Clinical Review`).
  get defaultLabel(): string {
    return startCase(this.kind);
  }

  // A person must open this card and act on it now.
  get isActionRequired(): boolean {
    return this.status === ActionStatus.ACTION_REQUIRED;
  }

  get isFlagged(): boolean {
    return this.status === ActionStatus.FLAGGED;
  }

  get isCompleted(): boolean {
    return this.status === ActionStatus.COMPLETED;
  }

  // Not yet anyone's problem — render it disabled, never hide it (design §12).
  get isPending(): boolean {
    return this.status === ActionStatus.PENDING;
  }
}

export class WorkflowValue {
  name: string;
  value: string | null;
  source: string;
  groupId: string | null;
  label: string | null;

  constructor(opts: Partial<WorkflowValueOptions> = {}) {
    this.name = opts.name ?? '';
    this.value = opts.value ?? null;
    this.source = opts.source ?? '';
    this.groupId = opts.groupId ?? null;
    this.label = opts.label ?? null;
  }
}

export class WorkflowDefinition {
  apiName: string;
  displayName: string;
  categories: WorkflowCategoryOptions[];

  constructor(opts: Partial<WorkflowDefinitionOptions> = {}) {
    this.apiName = opts.apiName ?? '';
    this.displayName = opts.displayName ?? '';
    this.categories = [...(opts.categories ?? [])].sort((a, b) => a.position - b.position);
  }
}

// Render an input value for the staleness diff: packet values are arrays of
// `{id, filename}` objects, so show their filenames; scalar values stringify. Returns
// null for an absent value so the diff can render an em dash.
function formatInputValue(value: unknown): string | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const formatted = value
      .map((entry) =>
        entry && typeof entry === 'object' && 'filename' in entry
          ? String((entry as { filename: string | null }).filename ?? '')
          : String(entry)
      )
      .filter((part) => part.length > 0)
      .join(', ');
    return formatted.length > 0 ? formatted : null;
  }
  return String(value);
}

export default class Workflow {
  id: string;
  status: WorkflowStatus;
  outcome: string;
  createdAt: Date | null;
  definition: WorkflowDefinition;
  actions: WorkflowAction[];
  values: WorkflowValue[];
  groups: WorkflowGroupOptions[];
  attachments: WorkflowAttachmentOptions[];

  constructor(options: Partial<WorkflowOptions> = {}) {
    const opts = { ...getDefaults(), ...options };
    this.id = opts.id;
    this.status = opts.status;
    this.outcome = opts.outcome;
    this.createdAt = parseDate(opts.createdAt) ?? null;
    this.definition = new WorkflowDefinition(opts.definition);
    // The API pre-sorts actions by (category position, entry position); keep that
    // order so the checklist reads the same every time (design §12).
    this.actions = opts.actions.map((a) => new WorkflowAction(a));
    this.values = opts.values.map((v) => new WorkflowValue(v));
    this.groups = opts.groups.map((g) => ({
      id: g.id ?? '',
      name: g.name ?? '',
      groupType: g.groupType ?? null,
    }));
    this.attachments = opts.attachments.map((a) => ({
      id: a.id ?? '',
      filename: a.filename ?? null,
      docType: a.docType ?? null,
    }));
  }

  // Terminal runs are frozen except the outcome (design §11) — used to stop polling.
  get isTerminal(): boolean {
    return this.status === WorkflowStatus.COMPLETED || this.status === WorkflowStatus.CANCELED;
  }

  get isCompleted(): boolean {
    return this.status === WorkflowStatus.COMPLETED;
  }

  get hasOutcome(): boolean {
    return !!this.outcome && this.outcome !== NO_OUTCOME;
  }

  // No dedicated patient-name value; compose it from the demographic values,
  // falling back to a neutral label before any data is entered.
  get patientName(): string {
    const first = this.valueFor('patient_first_name')?.value ?? '';
    const last = this.valueFor('patient_last_name')?.value ?? '';
    return `${first} ${last}`.trim() || 'New referral';
  }

  get groupNames(): string[] {
    return this.groups.map((g) => g.name).filter(Boolean);
  }

  // Coarse progress for the list page: completed vs. total expected actions.
  get progress(): { completed: number; total: number } {
    return {
      completed: this.actions.filter((a) => a.isCompleted).length,
      total: this.actions.length,
    };
  }

  // The current "pills" — most recent value per name, workflow-level + group-scoped.
  valueFor(name: string): WorkflowValue | undefined {
    return this.values.find((v) => v.name === name);
  }

  // Resolve "current" through `valueFor` (not a fresh name->value map) so the diff
  // agrees with the pill shown elsewhere when a name is duplicated across
  // workflow-level + group-scoped values (P1-1 contract §2).
  inputDiffFor(action: WorkflowAction): WorkflowInputDiff[] {
    return (
      action.inputs
        .map((input) => ({
          name: input.name,
          ranWithRaw: input.value as unknown,
          currentRaw: (this.valueFor(input.name)?.value ?? null) as unknown,
        }))
        // Deep-compare (object/array values aren't reference-equal), so an identical
        // packet doesn't read as changed.
        .filter((d) => JSON.stringify(d.ranWithRaw ?? null) !== JSON.stringify(d.currentRaw ?? null))
        .map((d) => ({
          name: d.name,
          ranWith: formatInputValue(d.ranWithRaw),
          current: formatInputValue(d.currentRaw),
        }))
    );
  }

  // Actions grouped under their category for display, in definition order. Actions
  // whose category is unknown fall into a trailing group so nothing is dropped.
  actionsByCategory(): { category: WorkflowCategoryOptions; actions: WorkflowAction[] }[] {
    return this.definition.categories.map((category) => ({
      category,
      actions: this.actions.filter((a) => a.categoryKey === category.key),
    }));
  }
}
